// CustomizeHub.jsx — E7 (super-marathon) unified Customize center.
//
// One door for all customization. A picker dialog lists the sections (customizeHubConfig);
// selecting one opens that section's EXISTING view modal (reused verbatim), and closing it
// returns to the picker — single-modal navigation, so no view is refactored or duplicated.
// Gated by apolo_customize_hub upstream (this component is only mounted when the flag is ON).

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { CUSTOMIZE_TABS } from './customizeHubConfig.js';

/**
 * @param {{ open: boolean, onClose: () => void }} props
 */
export default function CustomizeHub({ open, onClose }) {
  const [activeId, setActiveId] = useState(null);
  if (!open || typeof document === 'undefined') return null;

  const active = CUSTOMIZE_TABS.find((t) => t.id === activeId) || null;
  if (active) {
    const View = active.component;
    // Render the section's own modal; closing it returns to the picker (not the whole hub).
    // Portal em document.body pra escapar do UserStatusBar (backdrop-blur cria
    // containing block que prende `fixed inset-0`).
    return createPortal(<View open onClose={() => setActiveId(null)} />, document.body);
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Customizar"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-lg flex-col overflow-hidden rounded-apolo-lg border border-apolo-borderSubtle bg-apolo-surface1 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-apolo-borderSubtle px-4 py-3">
          <h2 className="text-sm font-medium text-apolo-textPrimary">Customizar</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded p-1 text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
        <ul className="flex flex-col gap-1 p-2">
          {CUSTOMIZE_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setActiveId(t.id)}
                  className="flex w-full items-center gap-3 rounded-apolo-md px-3 py-2 text-left transition hover:bg-apolo-surface2 focus:outline-none focus:ring-2 focus:ring-apolo-accent/50"
                >
                  <Icon size={16} className="shrink-0 text-apolo-textTertiary" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block text-sm text-apolo-textPrimary">{t.label}</span>
                    <span className="block text-[11px] text-apolo-textTertiary">{t.description}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
